import { RISK_LIKELIHOODS } from "../../types/risk";
import type { RiskImpact, RiskLikelihood, RiskMatrixCell } from "../../types/risk";

export const IMPACT_LABELS: Record<RiskImpact, string> = {
  catastrophic: "Catastrophic",
  critical: "Critical",
  considerable: "Considerable",
  significant: "Significant",
  minor: "Minor",
  negligible: "Negligible",
};

export const LIKELIHOOD_LABELS: Record<RiskLikelihood, string> = {
  very_unlikely: "Very unlikely",
  unlikely: "Unlikely",
  possible: "Possible",
  likely: "Likely",
  very_likely: "Very likely",
};

export type RiskBand = "low" | "medium" | "high" | "very_high";

// Least to most severe, for the legend.
export const RISK_BANDS: RiskBand[] = ["low", "medium", "high", "very_high"];

export const BAND_LABELS: Record<RiskBand, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  very_high: "Very High",
};

// Fixed heat-map grid: impact (rows, worst first) x likelihood (columns,
// least likely first). Hand-authored, not computed, so the banding is an
// exact, reviewable choice rather than a formula's side effect.
const GRID: Record<RiskImpact, RiskBand[]> = {
  catastrophic: ["high", "very_high", "very_high", "very_high", "very_high"],
  critical: ["medium", "high", "very_high", "very_high", "very_high"],
  considerable: ["medium", "high", "high", "very_high", "very_high"],
  significant: ["low", "medium", "high", "high", "very_high"],
  minor: ["low", "low", "medium", "high", "high"],
  negligible: ["low", "low", "low", "medium", "high"],
};

export function bandFor(impact: RiskImpact, likelihoodIndex: number): RiskBand {
  return GRID[impact][likelihoodIndex];
}

// Same lookup, but from a stored matrix_cell value directly (e.g. the
// mitigation table needs "is this risk's placed cell red?" without the
// caller re-deriving impact/likelihood-index by hand each time).
export function bandForCell(cell: RiskMatrixCell): RiskBand {
  const [impact, likelihood] = cell.split("__") as [RiskImpact, RiskLikelihood];
  return bandFor(impact, RISK_LIKELIHOODS.indexOf(likelihood));
}

// Flat, fully opaque fills -- deliberately NOT translucent, so the matrix
// reads as solid colored blocks (like a real risk matrix) instead of
// letting the page's background photo bleed through and muddy them. Muted/
// desaturated rather than neon-bright, to sit quietly in the dark theme.
export const BAND_COLORS: Record<RiskBand, { fill: string; fillHover: string; text: string }> = {
  low: { fill: "#3F7D4F", fillHover: "#4A9059", text: "#FFFFFF" },
  medium: { fill: "#B8952E", fillHover: "#CBA53A", text: "#1F2937" },
  high: { fill: "#BD6A2E", fillHover: "#CF7A3A", text: "#FFFFFF" },
  very_high: { fill: "#A33636", fillHover: "#B54141", text: "#FFFFFF" },
};
